import { createFileRoute } from '@tanstack/react-router'
import { RafobaseLogo } from '@/components/rafobase-logo'

export const Route = createFileRoute('/privacy')({
  component: PrivacyPolicyPage,
})

function PrivacyPolicyPage() {
  return (
    <div className="mx-auto max-w-3xl px-6 py-12">
      <div className="mb-8">
        <RafobaseLogo />
      </div>

      <h1 className="mb-2 text-3xl font-bold">Privacy Policy</h1>
      <p className="mb-8 text-sm text-muted-foreground">Last updated: March 21, 2026</p>

      <div className="prose prose-neutral dark:prose-invert max-w-none space-y-6 text-sm leading-relaxed">
        <section>
          <h2 className="text-lg font-semibold">1. Introduction</h2>
          <p>
            Rafobase ("we", "our", "us") operates a creator relationship management (CRM) platform
            that helps e-commerce brands manage influencer partnerships and track user-generated
            content. This Privacy Policy explains how we collect, use, and protect your information
            when you use our platform at base.rafo.work.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold">2. Information We Collect</h2>
          <p>
            <strong>Account Information:</strong> When you register, we collect your name, email
            address, and password.
          </p>
          <p>
            <strong>Instagram Business Data:</strong> When you connect your Instagram Business
            account via OAuth, we access:
          </p>
          <ul className="ml-4 list-disc space-y-1">
            <li>
              Your Instagram Business profile information (username, profile picture, account ID)
            </li>
            <li>Media where your account is mentioned by other users (posts, stories, reels)</li>
            <li>Engagement metrics on mentioned media (likes, comments, reach, impressions)</li>
            <li>Comments on your media for moderation purposes</li>
          </ul>
          <p>
            <strong>Usage Data:</strong> We collect standard analytics data such as pages visited,
            features used, and session duration.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold">3. How We Use Your Information</h2>
          <ul className="ml-4 list-disc space-y-1">
            <li>Display creator mentions and user-generated content in your dashboard</li>
            <li>Track engagement metrics to help you evaluate creator partnerships</li>
            <li>
              Store media files (images, videos) so they remain accessible after Instagram
              expiration
            </li>
            <li>Auto-link mentions to creators registered in your account</li>
            <li>Send notifications about new mentions and system updates</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold">4. Data Storage and Security</h2>
          <p>
            Your data is stored on Cloudflare infrastructure (D1 database, R2 object storage) with
            encryption at rest and in transit. Instagram access tokens are encrypted with
            AES-256-GCM before storage. We retain your data for as long as your account is active.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold">5. Third-Party Services</h2>
          <p>We integrate with the following third-party services:</p>
          <ul className="ml-4 list-disc space-y-1">
            <li>
              <strong>Meta/Instagram API:</strong> To access your Instagram Business account data as
              authorized by you
            </li>
            <li>
              <strong>Cloudflare:</strong> Infrastructure, hosting, and data storage
            </li>
            <li>
              <strong>Sentry:</strong> Error monitoring and performance tracking
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold">6. Data Sharing</h2>
          <p>
            We do not sell, rent, or share your personal data or Instagram data with third parties
            for their marketing purposes. Data is only shared with service providers necessary to
            operate our platform (as listed above).
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold">7. Your Rights</h2>
          <p>
            In accordance with applicable data protection laws (including LGPD), you have the right
            to:
          </p>
          <ul className="ml-4 list-disc space-y-1">
            <li>Access and export your data</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your data and account</li>
            <li>Disconnect your Instagram account at any time (which revokes our access)</li>
            <li>Withdraw consent for data processing</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold">8. Data Deletion</h2>
          <p>
            When you disconnect your Instagram account or delete your Rafobase account, we delete
            all associated Instagram data including stored media, access tokens, and mention
            records. You can disconnect your Instagram account at any time from the Settings page.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold">9. Contact</h2>
          <p>
            For questions about this Privacy Policy or to exercise your data rights, contact us at:{' '}
            <a href="mailto:privacy@rafo.work" className="underline">
              privacy@rafo.work
            </a>
          </p>
        </section>
      </div>
    </div>
  )
}
